/**
 * 选股页 - 多条件筛选沪深 A 股。
 *
 * 数据流：表单 -> StockApi.screen -> 后端 /api/screen
 *   后端：腾讯 qt(主) + 新浪(验证) + 腾讯 K 线(技术指标) + 东财 datacenter(EPS/ROE)
 * 选项：主板 / 行业(多选·可搜索) / 概念(多选·可搜索) / 市盈率 / 每股收益
 *       / 净资产收益率 / 涨跌幅 / 技术价位 / 均线位置 / 排除ST
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { Filter, Loader2, RefreshCw, AlertTriangle, Search, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtNum, changeColor, changeSign } from "@/lib/format";
import {
  StockApi, ApiError,
  type ScreenOptions, type ScreenParams, type ScreenResponse,
} from "@/services/api";

const CACHE_KEY = "guxun.picker";

const MARKETS = [
  { value: "", label: "全部沪深 A" },
  { value: "shzb", label: "沪市主板" },
  { value: "szb", label: "深市主板" },
  { value: "cyb", label: "创业板" },
  { value: "kcb", label: "科创板" },
];
const PRICE_LEVELS = [
  { value: "", label: "不限" },
  { value: "high", label: "高位（分位 >70%）" },
  { value: "mid", label: "中位（30%-70%）" },
  { value: "low", label: "低位（<30%）" },
];
const MA_OPTIONS = [
  { value: "", label: "不限" },
  { value: "above_ma20", label: "站上 MA20" },
  { value: "below_ma20", label: "跌破 MA20" },
  { value: "above_ma60", label: "站上 MA60" },
  { value: "below_ma60", label: "跌破 MA60" },
  { value: "above_ma120", label: "站上 MA120" },
  { value: "below_ma120", label: "跌破 MA120" },
];
const HOT_OPTIONS = [
  { value: "", label: "不限" },
  { value: "week", label: "近一周热点" },
  { value: "month", label: "近一月热点" },
];

const LEVEL_STYLE: Record<string, { text: string; border: string; bg: string; label: string }> = {
  high: { text: "text-bull", border: "border-bull/40", bg: "bg-bull/10", label: "高位" },
  mid: { text: "text-ink-300", border: "border-ink-600", bg: "bg-ink-800", label: "中位" },
  low: { text: "text-bear", border: "border-bear/40", bg: "bg-bear/10", label: "低位" },
};

const inputCls =
  "bg-ink-900 border border-ink-700 focus:border-gold/60 px-2.5 py-1.5 text-sm text-ink-100 outline-none w-full";
const labelCls = "text-[0.6875rem] text-ink-500 mb-1 block";

// ============================================================
// 可搜索多选下拉（输入文字实时过滤，多选，标签展示已选项）
// ============================================================
function SearchableMultiSelect({
  options, value, onChange, placeholder,
}: {
  options: { code: string; name: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // 点击外部关闭下拉
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const q = query.trim();
  const filtered = q ? options.filter(o => o.name.includes(q)) : options;
  const selectedObjs = options.filter(o => value.includes(o.code));

  const toggle = (code: string) => {
    onChange(value.includes(code) ? value.filter(c => c !== code) : [...value, code]);
    setQuery("");  // 选中/取消后清空输入，方便继续搜索下一个
  };

  return (
    <div className="relative" ref={ref}>
      <div
        className={cn(inputCls, "flex flex-wrap items-center gap-1 min-h-[38px] cursor-text")}
        onClick={() => setOpen(true)}
      >
        {selectedObjs.map(s => (
          <span key={s.code} className="label-tag text-gold border-gold/40 bg-gold/10 flex items-center gap-0.5 whitespace-nowrap">
            {s.name}
            <button
              onClick={(e) => { e.stopPropagation(); toggle(s.code); }}
              className="hover:text-bear flex items-center"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selectedObjs.length ? "" : placeholder}
          className="bg-transparent outline-none text-sm text-ink-100 flex-1 min-w-[80px] py-0.5"
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-auto bg-ink-900 border border-ink-700 shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-ink-500">无匹配项</div>
          ) : (
            filtered.map(o => (
              <div
                key={o.code}
                onClick={() => toggle(o.code)}
                className={cn(
                  "px-3 py-1.5 text-sm cursor-pointer hover:bg-ink-800 flex items-center justify-between",
                  value.includes(o.code) ? "text-gold bg-gold/5" : "text-ink-200"
                )}
              >
                <span>{o.name}</span>
                {value.includes(o.code) && <span className="text-gold text-xs">✓</span>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  market: "", board: [] as string[], concept: [] as string[],
  peMin: "", peMax: "", chgMin: "", chgMax: "",
  epsMin: "", epsMax: "", roeMin: "", roeMax: "",
  priceLevel: "", ma: "", hot: "", excludeSt: true as boolean, leaderOnly: false as boolean,
};

export default function StockPicker() {
  const [options, setOptions] = useState<ScreenOptions | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ScreenResponse | null>(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState("");

  // 挂载：拉板块选项 + 恢复上次筛选结果（避免返回后丢失）
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const o = await StockApi.screenOptions();
        if (alive) setOptions(o);
      } catch { /* 降级：板块下拉为空 */ }
    })();
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { result: r, form: f } = JSON.parse(cached);
        if (alive && r) { setResult(r); setForm({ ...EMPTY_FORM, ...f }); }
      } catch { /* ignore */ }
    }
    return () => { alive = false; };
  }, []);

  const set = (k: string, v: string | boolean | string[]) => setForm((f) => ({ ...f, [k]: v }));

  const doScreen = useCallback(async () => {
    setLoading(true); setError(""); setResult(null); setProgress("");
    const params: ScreenParams = {
      market: form.market || undefined,
      board: form.board.length ? form.board : undefined,
      concept: form.concept.length ? form.concept : undefined,
      peMin: form.peMin ? Number(form.peMin) : undefined,
      peMax: form.peMax ? Number(form.peMax) : undefined,
      chgMin: form.chgMin ? Number(form.chgMin) : undefined,
      chgMax: form.chgMax ? Number(form.chgMax) : undefined,
      epsMin: form.epsMin ? Number(form.epsMin) : undefined,
      epsMax: form.epsMax ? Number(form.epsMax) : undefined,
      roeMin: form.roeMin ? Number(form.roeMin) : undefined,
      roeMax: form.roeMax ? Number(form.roeMax) : undefined,
      priceLevel: form.priceLevel || undefined,
      ma: form.ma || undefined,
      hot: form.hot || undefined,
      excludeSt: form.excludeSt || undefined,
      leaderOnly: form.leaderOnly || undefined,
      limit: 50,
    };
    if (params.priceLevel || params.ma) {
      setProgress("正在拉取候选股 K 线计算均线/价位，约 10-30 秒...");
    }
    try {
      const r = await StockApi.screen(params);
      setResult(r);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ result: r, form }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "筛选失败");
    } finally {
      setLoading(false); setProgress("");
    }
  }, [form]);

  const reset = () => {
    setForm(EMPTY_FORM);
    setResult(null); setError("");
    sessionStorage.removeItem(CACHE_KEY);
  };

  const techOn = !!(form.priceLevel || form.ma);

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* 标题 */}
      <div className="flex items-center gap-2">
        <Filter size={18} className="text-gold" />
        <h1 className="font-serif text-xl text-ink-100">选股</h1>
        <span className="text-xs text-ink-500">多条件筛选沪深 A 股 · 腾讯行情 + 新浪交叉验证</span>
      </div>

      {/* 筛选表单 */}
      <section className="card p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>主板</label>
            <select className={inputCls} value={form.market} onChange={(e) => set("market", e.target.value)}>
              {MARKETS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>行业板块（同花顺·可多选·输入搜索）</label>
            <SearchableMultiSelect
              options={options?.industries || []}
              value={form.board}
              onChange={(v) => set("board", v)}
              placeholder="输入关键字，如：半导体"
            />
          </div>
          <div>
            <label className={labelCls}>概念板块（可多选·输入搜索）</label>
            <SearchableMultiSelect
              options={options?.concepts || []}
              value={form.concept}
              onChange={(v) => set("concept", v)}
              placeholder="输入关键字，如：华为"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <label className={labelCls}>市盈率(最小)</label>
            <input type="number" className={inputCls} placeholder="如 0" value={form.peMin} onChange={(e) => set("peMin", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>市盈率(最大)</label>
            <input type="number" className={inputCls} placeholder="如 30" value={form.peMax} onChange={(e) => set("peMax", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>今日涨跌幅%(最小)</label>
            <input type="number" className={inputCls} placeholder="如 -5" value={form.chgMin} onChange={(e) => set("chgMin", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>今日涨跌幅%(最大)</label>
            <input type="number" className={inputCls} placeholder="如 5" value={form.chgMax} onChange={(e) => set("chgMax", e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <label className={labelCls}>每股收益(最小)</label>
            <input type="number" className={inputCls} placeholder="如 0.5" value={form.epsMin} onChange={(e) => set("epsMin", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>每股收益(最大)</label>
            <input type="number" className={inputCls} placeholder="如 10" value={form.epsMax} onChange={(e) => set("epsMax", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>净资产收益率%(最小)</label>
            <input type="number" className={inputCls} placeholder="如 10" value={form.roeMin} onChange={(e) => set("roeMin", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>净资产收益率%(最大)</label>
            <input type="number" className={inputCls} placeholder="如 40" value={form.roeMax} onChange={(e) => set("roeMax", e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 items-end">
          <div>
            <label className={labelCls}>技术价位（需拉 K 线，较慢）</label>
            <select className={inputCls} value={form.priceLevel} onChange={(e) => set("priceLevel", e.target.value)}>
              {PRICE_LEVELS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>均线位置</label>
            <select className={inputCls} value={form.ma} onChange={(e) => set("ma", e.target.value)}>
              {MA_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>近期热点</label>
            <select className={inputCls} value={form.hot} onChange={(e) => set("hot", e.target.value)}>
              {HOT_OPTIONS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-2 pb-1.5">
            <div className="flex items-center gap-2">
              <input
                type="checkbox" id="excludeSt" checked={form.excludeSt}
                onChange={(e) => set("excludeSt", e.target.checked)}
                className="w-4 h-4 accent-gold bg-ink-900 border border-ink-700"
              />
              <label htmlFor="excludeSt" className="text-sm text-ink-200 cursor-pointer select-none">排除 ST 股</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" id="leaderOnly" checked={form.leaderOnly}
                onChange={(e) => set("leaderOnly", e.target.checked)}
                className="w-4 h-4 accent-gold bg-ink-900 border border-ink-700"
              />
              <label htmlFor="leaderOnly" className="text-sm text-ink-200 cursor-pointer select-none">仅行业龙头</label>
            </div>
          </div>
        </div>

        {techOn && (
          <div className="flex items-center gap-1.5 mt-3 text-[0.6875rem] text-gold/80">
            <Info size={11} /> 已选技术条件，将额外拉取候选股 K 线计算均线/价位，预计多耗时 10-30 秒
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button onClick={doScreen} disabled={loading} className="btn-gold text-sm flex items-center gap-1.5">
            {loading ? <><Loader2 size={14} className="animate-spin" />筛选中...</> : <><Search size={14} />开始筛选</>}
          </button>
          <button onClick={reset} disabled={loading} className="btn-ghost text-sm flex items-center gap-1.5">
            <RefreshCw size={14} />重置
          </button>
        </div>
      </section>

      {/* 错误 */}
      {error && (
        <div className="flex items-center gap-2 p-3 border border-bear/40 bg-bear/10 text-bear text-sm">
          <AlertTriangle size={14} />{error}
          <button onClick={() => setError("")} className="ml-auto text-bear/60 hover:text-bear">×</button>
        </div>
      )}

      {/* 进度 */}
      {progress && (
        <div className="flex items-center gap-2 p-3 border border-gold/30 bg-gold/5 text-gold text-sm">
          <Loader2 size={14} className="animate-spin" />{progress}
        </div>
      )}

      {/* 结果 */}
      {result && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-base text-ink-100 flex items-center gap-2 title-bar">
              筛选结果
              <span className="num text-xs text-ink-500">共 {result.total} 只</span>
              {result.techApplied && <span className="label-tag text-gold border-gold/40 bg-gold/10">含技术指标</span>}
            </h2>
          </div>
          {result.results.length === 0 ? (
            <div className="card p-6 text-center text-sm text-ink-400">无符合条件的股票，请放宽筛选条件</div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-xs whitespace-nowrap">
                <thead>
                  <tr className="text-ink-500 border-b border-ink-700">
                    <th className="text-left px-3 py-2.5 font-normal">代码 / 名称</th>
                    <th className="text-right px-3 py-2.5 font-normal">最新价</th>
                    <th className="text-right px-3 py-2.5 font-normal">涨跌幅</th>
                    <th className="text-right px-3 py-2.5 font-normal">PE</th>
                    <th className="text-right px-3 py-2.5 font-normal">PB</th>
                    <th className="text-right px-3 py-2.5 font-normal">EPS{result.results[0]?.reportDate ? `(${result.results[0].reportDate.slice(2,7)})` : ""}</th>
                    <th className="text-right px-3 py-2.5 font-normal">ROE%</th>
                    <th className="text-right px-3 py-2.5 font-normal">市值(亿)</th>
                    <th className="text-center px-3 py-2.5 font-normal">行业地位</th>
                    {result.techApplied && <th className="text-center px-3 py-2.5 font-normal">技术位</th>}
                    {result.techApplied && <th className="text-center px-3 py-2.5 font-normal">MA60</th>}
                    <th className="text-center px-3 py-2.5 font-normal">验证</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map((q) => {
                    const lv = q.priceLevel ? LEVEL_STYLE[q.priceLevel] : null;
                    return (
                      <tr key={q.code} className="border-b border-ink-800 hover:bg-ink-800/40 transition-colors">
                        <td className="px-3 py-2.5">
                          <div className="text-ink-100">{q.name}</div>
                          <div className="num text-[0.6875rem] text-ink-500">{q.code}</div>
                        </td>
                        <td className="num text-right px-3 py-2.5 text-ink-100">{fmtNum(q.price)}</td>
                        <td className={cn("num text-right px-3 py-2.5", changeColor(q.changePct))}>
                          {changeSign(q.changePct)}{fmtNum(q.changePct)}%
                        </td>
                        <td className="num text-right px-3 py-2.5 text-ink-300">{q.pe > 0 ? fmtNum(q.pe, 1) : "-"}</td>
                        <td className="num text-right px-3 py-2.5 text-ink-300">{q.pb > 0 ? fmtNum(q.pb, 2) : "-"}</td>
                        <td className="num text-right px-3 py-2.5 text-ink-300">{q.eps != null ? fmtNum(q.eps, 2) : "-"}</td>
                        <td className="num text-right px-3 py-2.5 text-ink-300">{q.roe != null ? fmtNum(q.roe, 1) : "-"}</td>
                        <td className="num text-right px-3 py-2.5 text-ink-300">{q.mktCap > 0 ? fmtNum(q.mktCap, 0) : "-"}</td>
                        <td className="text-center px-3 py-2.5">
                          {q.industryRank != null ? (
                            q.industryRank === 1 ? (
                              <span className="label-tag text-bull border-bull/40 bg-bull/10 whitespace-nowrap">★{q.industryName}龙头</span>
                            ) : (
                              <span className="text-[0.6875rem] text-ink-400 whitespace-nowrap">{q.industryName}·第{q.industryRank}</span>
                            )
                          ) : <span className="text-ink-600">-</span>}
                        </td>
                        {result.techApplied && (
                          <td className="text-center px-3 py-2.5">
                            {lv ? (
                              <span className={cn("label-tag", lv.text, lv.border, lv.bg)}>
                                {lv.label}{q.pctRank != null ? ` ${q.pctRank.toFixed(0)}%` : ""}
                              </span>
                            ) : <span className="text-ink-600">-</span>}
                          </td>
                        )}
                        {result.techApplied && (
                          <td className="text-center px-3 py-2.5">
                            {q.ma60 != null ? (
                              <span className={cn("num", q.price >= q.ma60 ? "text-bull" : "text-bear")}>
                                {q.price >= q.ma60 ? "↑" : "↓"} {fmtNum(q.ma60)}
                              </span>
                            ) : <span className="text-ink-600">-</span>}
                          </td>
                        )}
                        <td className="text-center px-3 py-2.5">
                          {q.verified === "warn"
                            ? <span className="text-gold" title="新浪复核价格偏差>0.5%">⚠</span>
                            : <span className="text-ink-500">✓</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* 空状态 */}
      {!result && !loading && !error && (
        <div className="card p-10 text-center">
          <Filter size={32} className="text-ink-600 mx-auto mb-3" />
          <div className="text-sm text-ink-400">设置筛选条件后点击「开始筛选」</div>
          <div className="text-[0.6875rem] text-ink-600 mt-1">
            数据源：腾讯行情（主）+ 新浪（交叉验证）+ 腾讯 K 线（技术指标）+ 东财 datacenter（EPS/ROE）
          </div>
        </div>
      )}
    </div>
  );
}
